import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  ACHIEVEMENTS, applyRound, badgeScore, dayKey, emptyLifetime, evaluate,
  type AchievementCtx, type Grade, type LifetimeStats, type RoundStats,
} from '@numslearn/shared';

/**
 * The solo-practice profile.
 *
 * Until a class session is running there is no server to hold a student's
 * history, so lifetime totals and earned badges live in localStorage — through
 * the very same `applyRound` / `evaluate` the server will call, so nothing has
 * to be re-derived when Phase 3 takes over.
 */

const STORAGE_KEY = 'numslearn.profile.v1';

export interface ProfileData {
  lifetime: LifetimeStats;
  /** Badge code → ISO timestamp it was earned. */
  earned: Record<string, string>;
}

const empty = (): ProfileData => ({ lifetime: emptyLifetime(), earned: {} });

function load(): ProfileData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty();
    const parsed = JSON.parse(raw) as Partial<ProfileData>;
    return {
      lifetime: { ...emptyLifetime(), ...(parsed.lifetime ?? {}) },
      earned: parsed.earned ?? {},
    };
  } catch {
    return empty();
  }
}

function save(data: ProfileData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* private window or storage disabled — the profile is simply session-only */
  }
}

export interface RoundEnd {
  round: RoundStats;
  grade: Grade;
  level: number;
}

interface Ctx {
  profile: ProfileData;
  earnedCodes: Set<string>;
  badgePoints: number;
  /** Total badges earned out of the whole catalogue. */
  counts: { held: number; total: number };
  /** Award any badges already true mid-round (combos, mostly). Returns new codes. */
  grantLive: (round: RoundStats, grade: Grade) => string[];
  /** Fold a finished round into the lifetime totals and award what it earned. */
  finishRound: (end: RoundEnd) => string[];
  /** Context for rendering progress bars against the current profile. */
  progressCtx: (grade: Grade) => AchievementCtx;
  reset: () => void;
}

const ProfileCtx = createContext<Ctx | null>(null);

const blankRound = (): RoundStats => ({
  score: 0, matches: 0, misses: 0, maxCombo: 0,
  addsUsed: 0, hintsUsed: 0, cleared: false, elapsedMs: 0,
});

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<ProfileData>(load);

  const earnedCodes = useMemo(() => new Set(Object.keys(profile.earned)), [profile.earned]);

  const award = useCallback((codes: string[]) => {
    if (!codes.length) return;
    setProfile((prev) => {
      const now = new Date().toISOString();
      const earned = { ...prev.earned };
      for (const c of codes) earned[c] ??= now;
      const next = { ...prev, earned };
      save(next);
      return next;
    });
  }, []);

  const grantLive = useCallback((round: RoundStats, grade: Grade): string[] => {
    const codes = evaluate(
      { round, lifetime: profile.lifetime, rank: null, playerCount: 1, previousRank: null, grade },
      earnedCodes,
    );
    award(codes);
    return codes;
  }, [award, earnedCodes, profile.lifetime]);

  const finishRound = useCallback((end: RoundEnd): string[] => {
    // Lifetime totals must be folded in BEFORE evaluating, or a badge like
    // "first clear" would not see the very board that earned it.
    const lifetime = applyRound(profile.lifetime, end, dayKey());
    const codes = evaluate(
      { round: end.round, lifetime, rank: null, playerCount: 1, previousRank: null, grade: end.grade },
      earnedCodes,
    );
    setProfile((prev) => {
      const now = new Date().toISOString();
      const earned = { ...prev.earned };
      for (const c of codes) earned[c] ??= now;
      const next = { lifetime, earned };
      save(next);
      return next;
    });
    return codes;
  }, [earnedCodes, profile.lifetime]);

  const progressCtx = useCallback((grade: Grade): AchievementCtx => ({
    round: blankRound(),
    lifetime: profile.lifetime,
    rank: null,
    playerCount: 1,
    previousRank: null,
    grade,
  }), [profile.lifetime]);

  const reset = useCallback(() => {
    const fresh = empty();
    save(fresh);
    setProfile(fresh);
  }, []);

  const value: Ctx = {
    profile,
    earnedCodes,
    badgePoints: badgeScore(earnedCodes),
    counts: { held: earnedCodes.size, total: ACHIEVEMENTS.length },
    grantLive,
    finishRound,
    progressCtx,
    reset,
  };
  return <ProfileCtx.Provider value={value}>{children}</ProfileCtx.Provider>;
}

export function useProfile(): Ctx {
  const ctx = useContext(ProfileCtx);
  if (!ctx) throw new Error('useProfile must be used inside <ProfileProvider>');
  return ctx;
}
